import { config } from '@/config';
import { isServer } from '@/helpers';
import { useEffect, useState } from 'react';
import useExternalScript from './useExternalScript';

const environment = config.env === 'production' || config.env === 'staging' ? 'PRODUCTION' : 'TEST';

const TIMEOUT_TIMER = 5000;

const GOOGLE_PAY_SESSION_STORAGE_KEY = 'canMakeGooglePayment';
const APPLE_PAY_SESSION_STORAGE_KEY = 'canMakeApplePayment';

const useWalletPayment = () => {
  const cachedCanMakeGooglePayment = Boolean(!isServer && sessionStorage.getItem(GOOGLE_PAY_SESSION_STORAGE_KEY));
  const cachedCanMakeApplePayment = Boolean(!isServer && sessionStorage.getItem(APPLE_PAY_SESSION_STORAGE_KEY));

  const [hasActiveApplePayPaymentMethod, setHasActiveApplePayPaymentMethod] = useState(cachedCanMakeApplePayment);
  const [hasActiveGooglePayPaymentMethod, setHasActiveGooglePayPaymentMethod] = useState(cachedCanMakeGooglePayment);
  const [isLoading, setIsLoading] = useState(!cachedCanMakeGooglePayment || !cachedCanMakeApplePayment);
  const [scriptLoaded] = useExternalScript({ src: 'https://pay.google.com/gp/p/js/pay.js', cleanup: false });

  useEffect(() => {
    if (!scriptLoaded) return;
    const promises = [];
    if (window?.google?.payments && !cachedCanMakeGooglePayment) {
      promises.push(setupGooglePay());
    }
    if (window?.ApplePaySession && !cachedCanMakeApplePayment) {
      // throws an error when not in https
      try {
        if (ApplePaySession.canMakePayments()) {
          promises.push(setupApplePay());
        }
      } catch (error) {}
    }

    Promise.all(promises)
      .catch((error) => console.error(error))
      .finally(() => setIsLoading(false));
  }, [scriptLoaded]);

  function setupApplePay(): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Apple Pay setup timed out.'));
      }, TIMEOUT_TIMER);

      ApplePaySession.canMakePaymentsWithActiveCard(config.applepay.merchantIdentifier)
        .then((canMakePaymentsWithActiveCard) => {
          clearTimeout(timeout);
          setHasActiveApplePayPaymentMethod(canMakePaymentsWithActiveCard);
          sessionStorage.setItem(APPLE_PAY_SESSION_STORAGE_KEY, `${canMakePaymentsWithActiveCard}`);
          resolve();
        })
        .catch((error) => {
          clearTimeout(timeout);
          reject(error);
        });
    });
  }

  function setupGooglePay(): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Google Pay setup timed out.'));
      }, TIMEOUT_TIMER);

      const paymentsClient = new window.google.payments.api.PaymentsClient({
        environment,
        merchantInfo: { merchantId: config.googlepay.merchantId },
      });

      paymentsClient
        .isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: [
            {
              type: 'CARD',
              parameters: {
                allowedAuthMethods: ['PAN_ONLY'],
                allowedCardNetworks: ['AMEX', 'DISCOVER', 'MASTERCARD', 'VISA'],
              },
            },
          ],
          existingPaymentMethodRequired: true,
        })
        .then((response) => {
          clearTimeout(timeout);
          setHasActiveGooglePayPaymentMethod(response.paymentMethodPresent);
          sessionStorage.setItem(GOOGLE_PAY_SESSION_STORAGE_KEY, `${response.paymentMethodPresent}`);
          resolve();
        })
        .catch((error) => {
          clearTimeout(timeout);
          reject(error);
        });
    });
  }

  return { hasActiveApplePayPaymentMethod, hasActiveGooglePayPaymentMethod, isLoading };
};

export default useWalletPayment;
